import React from 'react';
import Button from '../../components/Form/Button';
import { OffCrewType } from '../../types';
import {
  daysInMonth, weekdayLabel, cycleStatus, countWorkedDays, statusDisplay,
  OffCrewRow, offCrewTypeLabels, offCrewNamePlaceholders, tableInputCls,
} from './timesheetUtils';

interface OffCrewSectionProps {
  month: string; // yyyy-MM
  rows: OffCrewRow[];
  onChange: (key: string, row: OffCrewRow) => void;
  onAdd: (type: OffCrewType) => void;
  onRemove: (key: string) => void;
}

const typeBadgeCls: Record<OffCrewType, string> = {
  office: 'bg-blue-100 text-blue-800',
  visitor: 'bg-purple-100 text-purple-800',
  manpower: 'bg-orange-100 text-orange-800',
};

/** "Off Crew Personnel" grid: Office / Visitor / Manpower rows with day cells */
const OffCrewSection: React.FC<OffCrewSectionProps> = ({ month, rows, onChange, onAdd, onRemove }) => {
  const totalDays = daysInMonth(month);
  const dayNumbers = Array.from({ length: totalDays }, (_, i) => i + 1);

  const toggleDay = (row: OffCrewRow, day: number) => {
    const next = cycleStatus(row.days[String(day)]);
    onChange(row.key, { ...row, days: { ...row.days, [String(day)]: next } });
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Off Crew Personnel</h3>
          <p className="text-xs text-gray-500">
            Office: staff name + period on board · Visitor: name/count + organization (e.g. عدد 4 افراد ورشة ...) · Manpower: labor for specific works
          </p>
        </div>
        <div className="flex gap-2">
          {(['office', 'visitor', 'manpower'] as OffCrewType[]).map((type) => (
            <Button key={type} type="button" variant="secondary" size="sm" onClick={() => onAdd(type)}>
              + {offCrewTypeLabels[type]}
            </Button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-6">No off-crew personnel for this month</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="sticky left-0 bg-gray-50 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap z-10">Type / Name</th>
                {dayNumbers.map((day) => (
                  <th key={day} className="px-1 py-2 text-center text-xs font-medium text-gray-500">
                    <div>{day}</div>
                    <div className="text-[10px] text-gray-400">{weekdayLabel(month, day)}</div>
                  </th>
                ))}
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Days</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase min-w-[160px]">Description</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((row) => (
                <tr key={row.key} className="hover:bg-gray-50">
                  <td className="sticky left-0 bg-white px-4 py-2 whitespace-nowrap z-10">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mb-1 ${typeBadgeCls[row.type]}`}>
                      {offCrewTypeLabels[row.type]}
                    </span>
                    <input
                      type="text"
                      value={row.name}
                      onChange={(e) => onChange(row.key, { ...row, name: e.target.value })}
                      placeholder={offCrewNamePlaceholders[row.type]}
                      className={`${tableInputCls} min-w-[220px] block mt-1`}
                    />
                  </td>
                  {dayNumbers.map((day) => {
                    const status = row.days[String(day)] || 'off';
                    const display = statusDisplay[status];
                    return (
                      <td key={day} className="px-0.5 py-1 text-center">
                        <button
                          type="button"
                          title={`Day ${day}: ${display.title}`}
                          onClick={() => toggleDay(row, day)}
                          className={`w-7 h-7 rounded border text-xs font-bold ${display.cls} hover:opacity-75 transition-opacity`}
                        >
                          {display.symbol}
                        </button>
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-center font-semibold text-gray-900 whitespace-nowrap">
                    {countWorkedDays(row.days)}
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={row.description}
                      onChange={(e) => onChange(row.key, { ...row, description: e.target.value })}
                      placeholder="Notes..."
                      className={`${tableInputCls} min-w-[150px]`}
                    />
                  </td>
                  <td className="px-2 py-2 text-center">
                    <button
                      type="button"
                      onClick={() => onRemove(row.key)}
                      title="Remove row"
                      className="text-red-500 hover:text-red-700 font-bold text-lg leading-none"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OffCrewSection;
